// Onboarding: "Features you won't find in other apps" preview.
//
// Second of the two new native-only screens (see
// OnboardingFeaturesScreen.jsx for the first). Renders a 3-column
// comparison table using the differentiators from
// `RIVALS_ONBOARD` — every row Calofay has and (per owner spec) the
// generically-labelled "Other apps" column does not. **Competitor
// apps are never named** — Apple's guideline aside, the owner
// explicitly asked to keep the comparison generic.
//
// Layout mirrors the LandingV2 desktop table but in a mobile-first
// grid: Feature | Others (X) | Calofay (✓). The Calofay column is
// brand-orange to visually "win" every row.

import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, X, Trophy } from "lucide-react";
import { RIVALS_ONBOARD } from "@/lib/onboardingPreviewData";

const COPY = {
  ar: {
    eyebrow: "ماذا يميّز كالوفاي",
    title: "مميزات لن تجدها في التطبيقات الأخرى",
    colFeature: "الميزة",
    colOthers: "التطبيقات الأخرى",
    colUs: "كالوفاي",
    badge: "الأفضل",
    continue: "متابعة",
  },
  en: {
    eyebrow: "What makes Calofay different",
    title: "Features you won't find in other apps",
    colFeature: "Feature",
    colOthers: "Other apps",
    colUs: "Calofay",
    badge: "Best",
    continue: "Continue",
  },
};

export default function OnboardingCompetitorsScreen({ lang = "ar", onContinue }) {
  const isRTL = lang === "ar";
  const t = COPY[lang];

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[90] bg-neutral-50 flex flex-col"
      data-testid="onboarding-competitors-screen"
    >
      {/* Header */}
      <div
        className="relative px-5 pb-5 shadow-sm"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 20px)",
          background:
            "linear-gradient(180deg, #FFF7EE 0%, #FFEBD4 60%, #FFFFFF 100%)",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-500 text-white">
            <Trophy className="w-3.5 h-3.5" strokeWidth={2.5} />
          </div>
          <span className="text-[11px] font-black uppercase tracking-widest text-brand-600">
            {t.eyebrow}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-neutral-900 leading-tight">
          {t.title}
        </h1>
      </div>

      {/* Comparison table — scrollable so the floating CTA never
          overlaps content. `pb-32` mirrors the features screen. */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-32">
        <div className="max-w-md mx-auto">
          {/* Column headers */}
          <div
            className="grid grid-cols-[1.5fr_1fr_1fr] rounded-t-2xl overflow-hidden border border-neutral-200/70 border-b-0 bg-white"
            data-testid="rivals-header"
          >
            <div className="px-3 py-3 text-[11px] font-black text-neutral-500 uppercase tracking-wider flex items-center">
              {t.colFeature}
            </div>
            <div className="px-2 py-3 text-center">
              <div className="text-[11px] font-black text-neutral-500 leading-tight">
                {t.colOthers}
              </div>
            </div>
            <div className="px-2 py-3 text-center bg-brand-500 flex flex-col items-center justify-center gap-1">
              {/* Badge stacked INSIDE the cell (was `-top-1.5` and
                  clipped by the parent's `overflow-hidden`). Now it
                  sits above the label with proper breathing room. */}
              <span className="bg-white text-brand-600 text-[9px] font-black px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap leading-none">
                {t.badge}
              </span>
              <div className="text-white font-black text-[13px] leading-none">
                {t.colUs}
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="rounded-b-2xl overflow-hidden border border-neutral-200/70 bg-white">
            {RIVALS_ONBOARD.map((row, i) => {
              const notLast = i < RIVALS_ONBOARD.length - 1;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: isRTL ? 8 : -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.04, 0.35) }}
                  className="grid grid-cols-[1.5fr_1fr_1fr] items-stretch"
                >
                  <div
                    className={`px-3 py-3 text-[12.5px] text-neutral-800 leading-snug font-medium flex items-center ${
                      notLast ? "border-b border-neutral-100" : ""
                    }`}
                  >
                    {isRTL ? row.ar : row.en}
                  </div>
                  <div
                    className={`px-2 py-3 flex items-center justify-center ${
                      notLast ? "border-b border-neutral-100" : ""
                    }`}
                  >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rose-50 text-rose-400 border border-rose-200">
                      <X className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                  </div>
                  <div className="px-2 py-3 flex items-center justify-center bg-brand-500">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/25 text-white ring-1 ring-white/50">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating Continue CTA — identical treatment to the features
          screen so the two share a consistent visual anchor. */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 pointer-events-none"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)",
          paddingTop: "24px",
          background:
            "linear-gradient(180deg, rgba(250,250,250,0) 0%, rgba(250,250,250,0.85) 40%, rgba(250,250,250,1) 100%)",
        }}
      >
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button
            type="button"
            onClick={onContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="onboarding-competitors-continue"
          >
            {t.continue}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
